import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { PointOfInterest } from '@/__generated__/graphql';
import { BLISTER_TYPE, PIT_TYPE } from '@/constants';
import { useRouter } from '@/hooks';
import { checkIfVoi } from '@/utils/poi';
import * as state from '@/components/Analysis/state';
import * as poiState from '../../../state';
import { ClickEvent } from '@/types';

const DISALLOWED_TYPES = [BLISTER_TYPE, PIT_TYPE];

export const usePoiUpdateLocation = (poi: PointOfInterest) => {
  const setEditPointOfInterest = useSetAtom(poiState.editPointOfInterest);
  const setDrawerRight = useSetAtom(state.drawerRight);
  const allVoiTemplates = useAtomValue(state.allVoiTemplates);
  const router = useRouter();

  const isAtInsightsPage = useMemo(() => {
    return router.pathname.includes('insights');
  }, [router.pathname]);

  const poiHasLocation = useMemo(() => {
    const { centerPoint3d } = poi;
    if (!centerPoint3d) return false;
    if (centerPoint3d.x === 0 && centerPoint3d.y === 0 && centerPoint3d.z === 0) return false;
    return true;
  }, [poi]);

  const isDisallowedType = useMemo(() => {
    return DISALLOWED_TYPES.includes(poi.type ?? '');
  }, [poi.type]);

  const handleUpdateLocation = useCallback(
    (event: ClickEvent | undefined) => {
      if (isAtInsightsPage) {
        if (!event) return;

        const isCtrlClick = event.ctrlKey || event.metaKey;
        const { centerPoint3d } = poi;
        const cameraParams = centerPoint3d
          ? `&look.x=${centerPoint3d.x}&look.y=${centerPoint3d.y}&look.z=${
              centerPoint3d.z
            }&position.x=${centerPoint3d.x}&position.y=${centerPoint3d.y + 15}&position.z=${
              centerPoint3d.z + 5
            }&fov=0`
          : '';

        if (isCtrlClick) {
          const url = `/analysis/${router.query.inspection}/viewer?assembly=${poi.assembly.tagName}&poi=${poi.id}&isUpdatingPoiLocation=true${cameraParams}`;
          window.open(url, '_blank');
          return;
        } else {
          router.push({
            pathname: `/analysis/${router.query.inspection}/viewer`,
            query: {
              assembly: poi.assembly.tagName,
              isUpdatingPoiLocation: 'true',
              poi: poi.id,
              ...(centerPoint3d && {
                'look.x': String(centerPoint3d.x),
                'look.y': String(centerPoint3d.y),
                'look.z': String(centerPoint3d.z),
                'position.x': String(centerPoint3d.x),
                'position.y': String(centerPoint3d.y + 15),
                'position.z': String(centerPoint3d.z + 5),
                fov: '0',
              }),
            },
          });
        }

        return;
      }

      setDrawerRight({
        equipment: false,
        viewpoints: false,
        measuringTool: false,
        findViewpointsTool: false,
      });

      if (checkIfVoi(allVoiTemplates, poi?.type)) {
        const voiTemplate = allVoiTemplates?.find((template) => template.id === poi.templateId);
        setEditPointOfInterest((previous) => ({
          ...previous,
          template: voiTemplate,
          pointOfInterest: poi,
          formState: 'Resizing-VOI',
          state: 'WaitingForPoint',
        }));
      } else {
        setEditPointOfInterest((previous) => ({
          ...previous,
          formState: 'Update-Location',
          state: 'WaitingForPoint',
        }));
      }
    },
    [isAtInsightsPage, router, setDrawerRight, setEditPointOfInterest, poi, allVoiTemplates]
  );

  return { handleUpdateLocation, poiHasLocation, isDisallowedType };
};
